import React, { ReactNode, useEffect, useState, useRef } from "react";
import {FontAwesomeIcon}    from "@fortawesome/react-fontawesome";

import "./omni-search-carousel.scss";

const COUNT_DISPLAY_DEFAULT = 4
const COUNT_MOVEMENT_SWIPE = 1
const WIDTH_GAP_MIN = 10 

const DIVISOR_DEFAULT = 3

const MOVEMENT_SWIPE = {
    NONE: 0,
    INCREMENT: 1,
    DECREMENT: 2 
}

interface OmniSearchCarouselProps<T> {
    Component: React.ComponentType<{item: T}>
    items: T[]
    widthComponent: number
    edgeItemVisibilityDivisor?: number
    additionalProps?: P
}

export const OmniSearchCarousel = <T, P = {}>({ 
    Component,
    items,
    widthComponent,
    edgeItemVisibilityDivisor = DIVISOR_DEFAULT,
    additionalProps,
}) => {
    const [widthContainer, setWidthContainer] = useState(window.innerWidth)
    const [indexItem, setIndexItem] = useState(0)
    const [countVisible, setCountVisible] = useState(COUNT_DISPLAY_DEFAULT)
    const [xTranslate, setXTranslate] = useState(0)
    const [widthCarousel, setWidthCarousel] = useState(window.innerWidth)
    const [widthGap, setWidthGap] = useState(WIDTH_GAP_MIN)
    const [widthBlur, setWidthBlur] = useState(widthComponent / edgeItemVisibilityDivisor + WIDTH_GAP_MIN)
    const [shouldShowIncrement, setShouldShowIncrement] = useState(items.length > COUNT_DISPLAY_DEFAULT)
    const [shouldShowDecrement, setShouldShowDecrement] = useState(false)
    const [xTouch, setXTouch] = useState(null)
    const [movement, setMovement] = useState(MOVEMENT_SWIPE.NONE)
    const refContainer = useRef(null)
    const refCarousel = useRef(null)

    const widthOnScreenFraction = widthComponent / edgeItemVisibilityDivisor
    const widthOffScreenFraction = (edgeItemVisibilityDivisor - 1) * widthComponent / edgeItemVisibilityDivisor

    useEffect(() => {
        if(!refContainer.current) {
            return
        }

        const observer = new ResizeObserver(([container]) => {
            if(container && container.contentRect) {
                setWidthContainer(container.contentRect.width)
            }
        })

        observer.observe(refContainer.current)

        return () => observer.disconnect()
    }, [widthContainer])

    useEffect(() => {
        if(!widthContainer) {
            return 
        }

        const updateDimensions = () => {
            const overhang = (indexItem === 0 || (widthContainer < 2 * widthComponent))
                           ? widthOnScreenFraction
                           : 2 * widthOnScreenFraction

            const widthAvailable = widthContainer - overhang
            const count = Math.floor(widthAvailable / (widthComponent + WIDTH_GAP_MIN))
            const divisor = (indexItem === 0 || (widthContainer < 2 * widthComponent))
                            ? count
                            : count + 1
            const gapNaieve = Math.ceil((widthAvailable - (count * widthComponent)) / divisor)
            const gap = Math.max(gapNaieve, WIDTH_GAP_MIN)
            const carousel = items.length * widthComponent + (items.length - 1) * gap
           
            setWidthGap(gap)           
            setCountVisible(count)
            setWidthCarousel(carousel)
            setXTranslate(calculateTranslation())

            const inDangerOfBeingTrapped = indexItem !== 0
            if(!inDangerOfBeingTrapped) {
                setShouldShowIncrement(items.length > count)
            }
        }
    
        updateDimensions()
    }, [widthContainer, indexItem, items.length, widthComponent, shouldShowDecrement])

    useEffect(() => {
        const blur = widthOnScreenFraction + WIDTH_GAP_MIN
        
        setWidthBlur(blur)
        setXTranslate(calculateTranslation())
    }, [widthGap, widthContainer])

    const calculateTranslation = () => {
        if(indexItem === 0) {
            return 0
        }

        const translationNtoM = widthGap + widthComponent
        if(widthContainer < 2 * widthComponent) {
            setShouldShowDecrement(false)
            return -1 * indexItem * translationNtoM
        }

        setShouldShowDecrement(true)
        const translationZeroToOne = -1 * widthOffScreenFraction
        if(indexItem === 1) {
            return translationZeroToOne
        }

        const countNtoM = indexItem - 1
        return translationZeroToOne - countNtoM * translationNtoM
    }

    const incrementItem = (count) => {
        let index = indexItem + count
        if(index > items.length - 1) {
            index = 0
        }
        setIndexItem(index)
    }

    const decrementItem = (count) => {
        let index = indexItem - count
        if(index < 0) {
            index = 0
        }
        setIndexItem(index)
    }

    const handleTouchStart = (e) => {
        setXTouch(e.touches[0].clientX)
    }

    const handleTouchMove = (e) => {
        if(!xTouch) {
            return
        }
        const xDiff = xTouch - e.touches[0].clientX
        const movementType = xDiff > 0 ? MOVEMENT_SWIPE.INCREMENT : MOVEMENT_SWIPE.DECREMENT
        setMovement(movementType)
    }

    const handleTouchEnd = (e) => {
        if(!xTouch) {
            return
        }

        switch(movement) {
            case MOVEMENT_SWIPE.INCREMENT:
                incrementItem(COUNT_MOVEMENT_SWIPE)
                break
            case MOVEMENT_SWIPE.DECREMENT:
                decrementItem(COUNT_MOVEMENT_SWIPE)
                break
            case MOVEMENT_SWIPE.NONE:
            default:
                break    
        }
        
        setXTouch(null)
        setMovement(MOVEMENT_SWIPE.NONE)
    }

    return (
        <div className="carousel-container-e2718">
            <div 
                className="container"
                ref={refContainer}
            >
                <div 
                    className="carousel"
                    ref={refCarousel}
                    style={{
                        transform: `translateX(${xTranslate}px)`,
                        width: `${widthCarousel}px`
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onTouchCancel={handleTouchEnd}
                >
                    {
                        items.map((item, i) => {
                            return (
                                <Component key={i} {...{item}} {...additionalProps} />
                            )
                        })
                    }
                </div>
                {
                    (shouldShowDecrement && (xTranslate !== 0)) &&
                    <div 
                        className="blur decrement"
                        style={{ 
                            width: `${widthBlur}px`,
                        }}
                    > 
                        <FontAwesomeIcon
                            icon={require('@fortawesome/free-solid-svg-icons')['faChevronLeft']}
                            className='icon-chevron'
                            onClick={(e) => {
                                e.stopPropagation()
                                decrementItem(countVisible)
                            }}
                            xd-qa-type="button"
                            xd-qa-name="Back Arrow"
                        />
                    </div>
                }
                {
                    shouldShowIncrement &&
                    <div 
                        className="blur increment"
                        style={{ 
                            width: `${widthBlur}px`,
                        }}
                    > 
                        <FontAwesomeIcon
                            icon={require('@fortawesome/free-solid-svg-icons')['faChevronRight']}
                            className='icon-chevron'
                            onClick={(e) => {
                                e.stopPropagation()
                                incrementItem(countVisible)
                            }}
                            xd-qa-type="button"
                            xd-qa-name="Forward Arrow"
                        />
                    </div>
                }
            </div>
        </div>
    )
}